import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";

export default function Contact() {
  const form = useRef();
  
  // React States for managing messages and loading state
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [phoneError, setPhoneError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();
    setPhoneError(""); // Reset phone errors on new submission
    
    // 1. Validate Country Code
    const formData = new FormData(form.current);
    const phoneNumber = formData.get("phone").trim();

    if (phoneNumber) {
      // Regex checks if the string starts with '+' followed by 1 to 4 digits for country code
      const hasCountryCode = /^\+\d{1,4}/.test(phoneNumber);
      
      if (!hasCountryCode) {
        setPhoneError("Please include your country code (e.g., +91 for India).");
        return; // Halt form submission
      }
    }

    // 2. Trigger EmailJS if validation passes
    setIsSubmitting(true);
    emailjs
      .sendForm(
        "service_dmf83nl",
        "template_cxhl7f4", // Make sure this Template ID corresponds to your unified Admin Template
        form.current,
        "LyrCrgroJ266QMU-Y"
      )
      .then(() => {
        setFormStatus({
          type: "success",
          message: "Thank you for contacting ITSeva Hub. Our team will get back to you shortly.",
        });
        form.current.reset();
      })
      .catch(() => {
        setFormStatus({
          type: "error",
          message: "Failed to send your inquiry. Please try again later.",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="contact-page">
      {/* HERO */}
      <section className="contact-hero">
        <motion.div
          className="contact-left"
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="service-badge">Contact ITSeva Hub</div>
          <h1>Let’s Build Something Amazing Together</h1>
          <p>
            Contact our experts for NetSuite consulting, Salesforce solutions,
            and modern web development services.
          </p>

          <div className="contact-info">
            <div className="info-box">
              <FaEnvelope />
              <span>support@itseva-hub.com</span>
            </div>
            <div className="info-box">
              <FaPhoneAlt />
              <span>+91 74285 68338</span>
            </div>
            <div className="info-box">
              <FaMapMarkerAlt />
              <span>India</span>
            </div>
          </div>
        </motion.div>

        {/* FORM */}
        <motion.div
          className="contact-right"
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <form ref={form} onSubmit={sendEmail} className="contact-form">
            
            {/* 
              HIDDEN INPUTS:
              These fields provide values to the scheduling parameters in the 
              unified email template. Because this is the general contact form, 
              we pass safe placeholder strings.
            */}
            <input type="hidden" name="inquiry_type" value="New Website Inquiry Received" />
            <input type="hidden" name="date" value="Not Scheduled (Contact Form)" />
            <input type="hidden" name="time_slot" value="N/A" />
            <input type="hidden" name="time_zone" value="N/A" />
            <input type="hidden" name="preferred_meeting" value="N/A" />

            <div className="form-grid">
              <input type="text" name="user_name" placeholder="Full Name" required />
              <input type="email" name="user_email" placeholder="Email Address" required />
              
              {/* Phone Field with native hover tooltip */}
              <div className="phone-input-container" style={{ position: "relative" }}>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number (e.g., +91 98765 43210)"
                  title="Please include your country code starting with '+' (e.g., +91 for India)"
                />
                {phoneError && (
                  <span className="field-error-msg" style={{ color: "#ff4d4d", fontSize: "0.85rem", display: "block", marginTop: "4px" }}>
                    {phoneError}
                  </span>
                )}
              </div>

              <input type="text" name="company" placeholder="Company Name" />
              <input type="text" name="address" placeholder="Address" />

              <select name="service">
                <option>NetSuite Consulting</option>
                <option>Salesforce Solutions</option>
                <option>Web Development</option>
              </select>
            </div>

            <textarea
              name="message"
              rows="6"
              placeholder="Tell us about your project..."
              required
            ></textarea>

            <button type="submit" className="primary-btn submit-btn" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Inquiry"}
            </button>

            {/* Render submission responses cleanly in React */}
            <div id="form-message" style={{ marginTop: "15px" }}>
              {formStatus.type === "success" && (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h3>Message Sent Successfully!</h3>
                  <p>{formStatus.message}</p>
                </div>
              )}
              {formStatus.type === "error" && (
                <div className="error-message">
                  <div className="error-icon">!</div>
                  <h3>Something Went Wrong</h3>
                  <p>{formStatus.message}</p>
                </div>
              )}
            </div>
          </form>
        </motion.div>
      </section>
    </div>
  );
}